import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createSortedRowModel, flexRender, rowSortingFeature, sortFn_alphanumeric, sortFn_basic, sortFn_datetime, sortFn_text, tableFeatures, useTable, } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { Skeleton } from "../atoms/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRoot, TableRow, } from "../atoms/table";
import { cn } from "../../lib/cn";
/**
 * The feature set, declared once at module scope.
 *
 * TanStack Table v9 is opt in: nothing is in the bundle unless it is named
 * here, which is why the sorting functions are imported one at a time rather
 * than pulled in as a bag. Adding filtering or pagination to this table means
 * adding the feature and its row model here, and the type of every column
 * definition follows automatically.
 *
 * It must be static. Building it inside the component would hand `useTable` a
 * new feature object on every render.
 */
const features = tableFeatures({
    rowSortingFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        basic: sortFn_basic,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});
/**
 * A sortable table over TanStack Table v9.
 *
 * TanStack is headless: it owns the row model, the sort state and the column
 * definitions, and renders nothing. That is the reason to use it here rather
 * than a component library's grid, because every pixel is still the brand's
 * own `Table` atoms.
 *
 * Sorting is announced properly, which is the part hand-rolled tables get
 * wrong. The header cell carries `aria-sort` with the real value, and the
 * control inside it is a `<button>` with a label saying what pressing it will
 * do, rather than a clickable `<th>` that no keyboard can reach.
 *
 * Sort state is local. A table whose sort belongs in the URL should lift it,
 * which is why `columns` and `data` are the only required props: this
 * component is not trying to own your query.
 */
export function DataTable({ columns, data, empty, loading = false, loadingRows = 5, caption, className, }) {
    const [sorting, setSorting] = useState([]);
    const table = useTable({
        features,
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
    });
    const rows = table.getRowModel().rows;
    if (!loading && rows.length === 0 && empty !== undefined) {
        return _jsx(_Fragment, { children: empty });
    }
    return (_jsx(TableRoot, { "data-slot": "data-table", className: cn(className), children: _jsxs(Table, { children: [caption !== undefined && _jsx("caption", { className: "lg:sr-only", children: caption }), _jsx(TableHeader, { children: table.getHeaderGroups().map((headerGroup) => (_jsx(TableRow, { children: headerGroup.headers.map((header) => {
                            const sortable = header.column.getCanSort();
                            const direction = header.column.getIsSorted();
                            const label = header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext());
                            return (_jsx(TableHead, { "aria-sort": direction === "asc"
                                    ? "ascending"
                                    : direction === "desc"
                                        ? "descending"
                                        : sortable
                                            ? "none"
                                            : undefined, children: sortable && !header.isPlaceholder ? (_jsxs("button", { type: "button", onClick: header.column.getToggleSortingHandler(), className: cn("lg:inline-flex lg:cursor-pointer lg:items-center lg:gap-1.5", "lg:rounded-xs lg:uppercase lg:transition-colors", "lg:hover:text-strong lg:outline-none", "lg:focus-visible:ring-2 lg:focus-visible:ring-ring", direction !== false && "lg:text-strong"), children: [label, direction === "asc" ? (_jsx(ArrowUp, { className: "lg:size-3", "aria-hidden": "true" })) : direction === "desc" ? (_jsx(ArrowDown, { className: "lg:size-3", "aria-hidden": "true" })) : (_jsx(ChevronsUpDown, { className: "lg:size-3 lg:opacity-50", "aria-hidden": "true" })), _jsx("span", { className: "lg:sr-only", children: direction === "asc"
                                                ? ", sorted ascending"
                                                : direction === "desc"
                                                    ? ", sorted descending"
                                                    : ", sort by this column" })] })) : (label) }, header.id));
                        }) }, headerGroup.id))) }), _jsx(TableBody, { children: loading
                        ? Array.from({ length: loadingRows }, (_, rowIndex) => (_jsx(TableRow, { children: Array.from({ length: columns.length }, (__, cellIndex) => (_jsx(TableCell, { children: _jsx(Skeleton, { className: "lg:h-4 lg:w-full" }) }, `skeleton-${String(rowIndex)}-${String(cellIndex)}`))) }, `skeleton-${String(rowIndex)}`)))
                        : rows.map((row) => (_jsx(TableRow, { children: row.getAllCells().map((cell) => (_jsx(TableCell, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id))) }, row.id))) })] }) }));
}
//# sourceMappingURL=data-table.js.map